/**
 * Tenant Service
 * Handles all tenant-related API calls
 */

import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  TenantResponseDto,
  CreateTenantDto,
  UpdateTenantDto,
  UpdateTenantStatusDto,
  TenantAPIResponse,
  TenantsListParams,
  PaginatedTenantsResponse,
  TenantMetrics,
  GitLabIngressHistory,
} from '@/types/tenant.types';

class TenantService {
  /**
   * Get all tenants with pagination and filters
   */
  async getAllTenants(
    params: TenantsListParams = {}
  ): Promise<PaginatedTenantsResponse> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<PaginatedTenantsResponse>
      >(API_ENDPOINTS.TENANTS.LIST, { params });

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get tenant by tenant key
   */
  async getTenantByKey(tenant_key: string): Promise<TenantResponseDto> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<TenantResponseDto>
      >(API_ENDPOINTS.TENANTS.GET_BY_KEY(tenant_key));

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create new tenant
   */
  async createTenant(
    tenantData: CreateTenantDto
  ): Promise<TenantResponseDto> {
    try {
      const response = await apiClient.post<
        TenantAPIResponse<TenantResponseDto>
      >(API_ENDPOINTS.TENANTS.CREATE, tenantData);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update tenant by tenant key
   */
  async updateTenant(
    tenant_key: string,
    tenantData: UpdateTenantDto
  ): Promise<TenantResponseDto> {
    try {
      const response = await apiClient.put<
        TenantAPIResponse<TenantResponseDto>
      >(API_ENDPOINTS.TENANTS.UPDATE(tenant_key), tenantData);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update tenant status
   */
  async updateTenantStatus(
    tenant_key: string,
    statusData: UpdateTenantStatusDto
  ): Promise<TenantResponseDto> {
    try {
      const response = await apiClient.patch<
        TenantAPIResponse<TenantResponseDto>
      >(API_ENDPOINTS.TENANTS.UPDATE_STATUS(tenant_key), statusData);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get tenant metrics (total, active, inactive, suspended)
   * Not affected by filters
   */
  async getTenantMetrics(): Promise<TenantMetrics> {
    try {
      const response = await apiClient.get<TenantAPIResponse<TenantMetrics>>(
        API_ENDPOINTS.TENANTS.METRICS
      );

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get management tenant info — backend reads MANAGEMENT_KEY_NAME from env
   * and returns the matching tenant document from the Tenants collection.
   */
  async getManagementTenantInfo(): Promise<{
    management_key: string;
    tenant: TenantResponseDto;
  }> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<{ management_key: string; tenant: TenantResponseDto }>
      >(API_ENDPOINTS.TENANTS.MANAGEMENT_INFO);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Delete tenant by tenant key
   */
  async deleteTenant(tenant_key: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<
        TenantAPIResponse<{ success: boolean; message: string }>
      >(API_ENDPOINTS.TENANTS.DELETE(tenant_key));

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Check if tenant key is available
   */
  async checkTenantKeyAvailability(tenant_key: string): Promise<{ available: boolean; message: string }> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<{ available: boolean; message: string }>
      >(API_ENDPOINTS.TENANTS.CHECK_AVAILABILITY(tenant_key));

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Check if tenant name is available
   */
  async checkTenantNameAvailability(tenant_name: string): Promise<{ available: boolean; message: string }> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<{ available: boolean; message: string }>
      >(API_ENDPOINTS.TENANTS.CHECK_NAME_AVAILABILITY(tenant_name));

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * View current ingress YAML from GitLab
   */
  async viewIngressYAML(): Promise<{ yaml: string }> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<{ yaml: string; message: string }>
      >(API_ENDPOINTS.TENANTS.VIEW_INGRESS_YAML);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Manually edit and commit ingress YAML
   */
  async editIngressYAML(yaml: string, commit_message: string): Promise<{
    success: boolean;
    commit_id: string;
    commit_url: string;
    message: string;
  }> {
    try {
      const response = await apiClient.put<
        TenantAPIResponse<{
          success: boolean;
          commit_id: string;
          commit_url: string;
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.EDIT_INGRESS_YAML, {
        yaml,
        commit_message
      });

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Manually sync tenant to GitLab ingress
   */
  async syncTenantToIngress(tenant_key: string): Promise<{
    success: boolean;
    commit_id: string;
    commit_url: string;
    action: 'added' | 'already_exists';
    message: string;
  }> {
    try {
      const response = await apiClient.post<
        TenantAPIResponse<{
          success: boolean;
          commit_id: string;
          commit_url: string;
          action: 'added' | 'already_exists';
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.SYNC_INGRESS(tenant_key));

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get ingress sync history for a tenant
   */
  async getIngressHistory(tenant_key: string): Promise<{
    history: GitLabIngressHistory[];
    total: number;
  }> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<{
          history: GitLabIngressHistory[];
          total: number;
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.INGRESS_HISTORY(tenant_key));

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * View all CloudFlare DNS records
   */
  async viewCloudFlareDNS(): Promise<{
    records: any[];
    total: number;
  }> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<{
          records: any[];
          total: number;
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.VIEW_CLOUDFLARE_DNS);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get CloudFlare DNS records for a specific tenant
   */
  async getTenantDNSRecords(tenant_key: string): Promise<{
    records: any[];
  }> {
    try {
      const response = await apiClient.get<
        TenantAPIResponse<{
          records: any[];
          tenant_key: string;
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.GET_TENANT_DNS(tenant_key));

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create a new DNS record manually
   */
  async createDNSRecord(data: {
    type: string;
    name: string;
    content: string;
    ttl?: number;
    proxied?: boolean;
  }): Promise<any> {
    try {
      const response = await apiClient.post<
        TenantAPIResponse<{
          record: any;
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.CREATE_DNS_RECORD, data);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update an existing DNS record manually
   */
  async updateDNSRecord(
    record_id: string,
    data: {
      type: string;
      name: string;
      content: string;
      ttl?: number;
      proxied?: boolean;
    }
  ): Promise<any> {
    try {
      const response = await apiClient.patch<
        TenantAPIResponse<{
          record: any;
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.UPDATE_DNS_RECORD(record_id), data);

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Delete a DNS record manually
   */
  async deleteDNSRecord(record_id: string): Promise<void> {
    try {
      const response = await apiClient.delete<
        TenantAPIResponse<{
          success: boolean;
          message: string;
        }>
      >(API_ENDPOINTS.TENANTS.DELETE_DNS_RECORD(record_id));

      if (!response.data.success) {
        throw response.data;
      }
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Seed tenant with default data (groups, roles, admin user, templates)
   */
  async seedTenant(
    tenant_key: string,
    data?: {
      full_name?: string;
      admin_email?: string;
      admin_password?: string;
      clear_data_first?: boolean;
      copy_templates?: boolean;
    }
  ): Promise<{
    admin_user_id: string;
    message: string;
    tenant_key: string;
    data_cleared: boolean;
  }> {
    try {
      const response = await apiClient.post<
        TenantAPIResponse<{
          admin_user_id: string;
          message: string;
          tenant_key: string;
          data_cleared: boolean;
        }>
      >(API_ENDPOINTS.TENANTS.SEED_TENANT, {
        tenant_key,
        ...(data || {})
      });

      if (!response.data.success) {
        throw response.data;
      }

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

}
// Export singleton instance
export default new TenantService();
